/**
 * Site Health → Google Business Profile → AUDIT tab.
 *
 * The scan tab asks "where does this client rank". This asks "is the profile
 * itself any good": name/address/phone against the website, description and
 * categories, attributes, photos, and unanswered reviews.
 *
 * The reads are free — the vendor's profile endpoints spend no credits — so
 * this loads with the page. A stored run inside the reuse window is reused so
 * that the HISTORY stays legible; "Re-check" forces a fresh read.
 *
 * Four states must never collapse into one another, which is most of the
 * markup below: not connected · connected but unmapped · connected but the
 * client never linked their Google account · read failed. Rendering any of
 * them as an empty findings list would tell an account manager a neglected
 * profile is flawless.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertCircle,
	CheckCircle2,
	Link2Off,
	RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import GbpRecheckBanner from "./GbpRecheckBanner";
import GbpWaitState from "./GbpWaitState";
import { useElapsedSeconds } from "./useElapsedSeconds";
import { gbpAuditLoadingStage } from "./gbpAuditLoadingCopy";
import GbpChecklistTopic from "./GbpChecklistTopic";
import GbpTopicRail from "./GbpTopicRail";
import GbpProfileCard from "./GbpProfileCard";
import {
	gbpAuditService,
	type GbpAuditResult,
	type GbpChecklistItem,
} from "@/services/gbpAuditService";

interface Props {
	tenantId: string;
	placeId?: string;
	/** Re-open one stored audit instead of running a fresh one. */
	runId?: string;
	/** Clears the stored-run selection. Only rendered when `runId` is set. */
	onBackToLatest?: () => void;
	/** Which topic the rail has selected. Empty = pick the one needing work. */
	section?: string;
	onSectionChange?: (section: string) => void;
}

function Panel({ children }: { children: React.ReactNode }) {
	return (
		<div className="rounded-lg border border-coral/30 bg-coral-light p-4 text-sm">
			{children}
		</div>
	);
}

/** What to tell the user when a decision does not stick.
 *
 *  The staging incident (2026-09-17) returned 200 OK with the write lost, so
 *  "it failed" is not always available — the caller also checks the ok flag.
 *  A 403 is worth naming outright: the checklist renders for anyone with
 *  access to the client, but recording a decision needs operator rights, and
 *  a silent no-op is an awful way to learn that.
 */
function describeDecideFailure(err: unknown): string {
	const status = (err as { response?: { status?: number } })?.response?.status;
	if (status === 403) {
		return "You do not have permission to record decisions on this checklist. Ask an operator to sign it off.";
	}
	if (status === 401) {
		return "Your session has expired. Reload the page and sign in again.";
	}
	if (status === 422) {
		return "The decision was rejected as malformed. This is a bug — please report it.";
	}
	return "The decision could not be saved. Nothing was changed.";
}

export default function GbpAuditPanel({
	tenantId,
	placeId = "",
	runId = "",
	onBackToLatest,
	section = "",
	onSectionChange,
}: Props) {
	const queryClient = useQueryClient();
	const [pendingItem, setPendingItem] = useState<string | null>(null);
	// The page lifts the choice into ?section= so it can be linked to, but
	// the panel must still work on its own — a controlled value with no
	// setter would leave the rail inert wherever it is used without one.
	const [localSection, setLocalSection] = useState("");

	const { data, isLoading, isError, refetch, isFetching } =
		useQuery<GbpAuditResult>({
			queryKey: ["site-health", "gbp-audit", tenantId, placeId, runId],
			queryFn: () => gbpAuditService.get(tenantId, placeId, false, runId),
			enabled: Boolean(tenantId),
		});

	// A decision that fails must SAY so. Until now there was no onError and
	// nothing read the ok flag, so a refusal — a 403 from the operator gate,
	// a 422, a failed insert — cleared the spinner, refetched, left the row
	// unchanged and told the user nothing. On staging every click was
	// refused and the page looked simply unresponsive (2026-09-17).
	const [decideError, setDecideError] = useState<string | null>(null);

	const decide = useMutation({
		mutationFn: (vars: {
			itemId: string;
			status: "approved" | "declined" | "reopened" | "flagged";
		}) =>
			gbpAuditService.decide({
				tenantId,
				itemId: vars.itemId,
				status: vars.status,
				placeId,
			}),
		onMutate: (vars) => {
			setDecideError(null);
			setPendingItem(vars.itemId);
		},
		onSuccess: (result) => {
			// 200 does not mean saved: record_decision reports a refusal in
			// the body.
			if (!result?.ok) {
				setDecideError(
					result?.error || "The decision could not be saved.",
				);
			}
		},
		onError: (err: unknown) => {
			setDecideError(describeDecideFailure(err));
		},
		onSettled: () => {
			setPendingItem(null);
			// The decision changes which items count as failures, so the whole
			// audit is refetched rather than patched locally.
			queryClient.invalidateQueries({
				queryKey: ["site-health", "gbp-audit", tenantId, placeId, runId],
			});
		},
	});

	/* isFetching, not isLoading: a re-check keeps the previous result on
	   screen, so it is not "loading" — but it is the same wait and needs
	   the same clock.
	 *
	 * rechecking as well: the forced read happens in a direct service call,
	 * OUTSIDE React Query, so isFetching stays false for the 10-35 seconds it
	 * takes. Relying on it alone left the button neither disabled nor
	 * spinning during the only slow part of a re-check (2026-09-23). */
	const [rechecking, setRechecking] = useState(false);
	const waiting = isFetching || rechecking;
	const elapsedSeconds = useElapsedSeconds(waiting);

	if (isLoading) {
		return (
			<GbpWaitState
				stage={gbpAuditLoadingStage(elapsedSeconds)}
				testId="gbp-audit-loading"
			/>
		);
	}

	if (!data.connected) {
		return (
			<Panel>
				<p className="font-medium">Profile scanning isn't connected yet.</p>
				<p className="mt-1 text-muted-foreground">
					Once this client's listing is connected, the audit runs here
					automatically.
				</p>
			</Panel>
		);
	}

	if (!data.mapped) {
		return (
			<Panel>
				<p className="font-medium">
					No Business Profile listing is assigned to this client yet.
				</p>
				<p className="mt-1 text-muted-foreground">
					Assign one and the audit will run against it.
				</p>
			</Panel>
		);
	}

	if (!data.readOk) {
		return (
			<div className="space-y-3">
				<Panel>
					<p className="font-medium">{data.error}</p>
					<p className="mt-1 text-muted-foreground">
						Nothing has been reported as fixed — a failed check is recorded
						as a gap in the history, never as a clean result.
					</p>
				</Panel>
				<Button variant="outline" size="sm" onClick={() => refetch()}>
					<RefreshCw className="h-3.5 w-3.5 mr-1.5" />
					Try again
				</Button>
			</div>
		);
	}

	// Topic order comes from the checklist itself, so the backend owns it.
	const sectionOrder: string[] = [];
	for (const item of data.checklist) {
		if (!sectionOrder.includes(item.section)) sectionOrder.push(item.section);
	}
	const bySection: Record<string, GbpChecklistItem[]> = {};
	for (const item of data.checklist) {
		(bySection[item.section] ||= []).push(item);
	}
	const topics: [string, string][] = sectionOrder.map((key) => [
		key,
		data.sections[key] ?? key,
	]);

	// Land on the first topic that wants attention rather than always the
	// first alphabetically — the operator opened this to find work.
	const firstWithWork =
		sectionOrder.find((key) =>
			(bySection[key] ?? []).some((i) => i.status === "fail"),
		) ??
		sectionOrder.find((key) =>
			(bySection[key] ?? []).some((i) => i.status === "unknown"),
		) ??
		sectionOrder[0] ??
		"";
	const chosen = section || localSection;
	const activeTopic = sectionOrder.includes(chosen) ? chosen : firstWithWork;

	const resolved = data.diff?.resolved ?? [];
	const appeared = data.diff?.new ?? [];

	return (
		<div className="space-y-6" data-testid="gbp-audit">
			<div className="flex items-start justify-between gap-4 flex-wrap">
				<div>
					<p className="text-sm font-medium">{data.summary}</p>
					<p className="text-xs text-muted-foreground mt-0.5">
						{data.locationName}
						{data.generatedAt &&
							` · checked ${new Date(data.generatedAt).toLocaleString()}`}
						{data.reused && " · reusing the last check"}
					</p>
					{waiting && (
						<GbpRecheckBanner
							elapsedSeconds={elapsedSeconds}
							testId="gbp-audit-refetching"
						/>
					)}
				</div>
				{!runId && (
				<Button
					variant="outline"
					size="sm"
					onClick={() => {
						setRechecking(true);
						gbpAuditService
							.get(tenantId, placeId, true)
							.then(() => refetch())
							.finally(() => setRechecking(false));
					}}
					disabled={waiting}
				>
					<RefreshCw
						className={
							waiting
								? "h-3.5 w-3.5 mr-1.5 animate-spin"
								: "h-3.5 w-3.5 mr-1.5"
						}
					/>
					Re-check
				</Button>
				)}
			</div>

			{runId && (
				<div
					className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-coral/40 bg-coral-light p-3 text-sm"
					data-testid="gbp-viewing-past-audit"
				>
					<span className="text-coral-dark">
						Showing a saved audit
						{data.generatedAt
							? ` from ${new Date(data.generatedAt).toLocaleDateString()}`
							: ""}
						. Nothing here was re-checked just now.
					</span>
					{onBackToLatest && (
						<Button variant="outline" size="sm" onClick={onBackToLatest}>
							Back to the latest audit
						</Button>
					)}
				</div>
			)}

			<GbpProfileCard
				profile={data.profile}
				historical={Boolean(runId)}
			/>

			{!data.gbpLinked && (
				<div className="rounded-lg border border-border bg-muted/40 p-3 flex gap-2">
					<Link2Off className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
					<div className="text-sm">
						<p className="font-medium">Partial audit</p>
						<p className="text-muted-foreground">{data.note}</p>
					</div>
				</div>
			)}

			<div className="flex gap-3 flex-wrap">
				{[
					{
						label: "To fix",
						value: data.checklistCounts.fail,
						tone: "critical",
					},
					{
						label: "Need you",
						value: data.checklistCounts.manual,
						tone: "warning",
					},
					{ label: "Passed", value: data.checklistCounts.pass, tone: "plain" },
					{
						label: "Not checked",
						value: data.checklistCounts.unknown,
						tone: "plain",
					},
				].map((stat) => (
					<div
						key={stat.label}
						className="rounded-lg border border-border px-3 py-2 min-w-[92px]"
					>
						<p
							className={
								stat.tone === "critical" && stat.value > 0
									? "text-xl font-semibold text-destructive"
									: stat.tone === "warning" && stat.value > 0
										? "text-xl font-semibold text-[hsl(var(--status-warning))]"
										: "text-xl font-semibold"
							}
						>
							{stat.value}
						</p>
						<p className="text-xs text-muted-foreground">{stat.label}</p>
					</div>
				))}
			</div>

			{(resolved.length > 0 || appeared.length > 0) && (
				<div className="rounded-lg border border-border p-3 space-y-1.5">
					<p className="text-xs uppercase tracking-wide text-muted-foreground">
						Since the last check
					</p>
					{resolved.length > 0 && (
						<p className="text-sm flex items-center gap-1.5">
							<CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-success))]" />
							{resolved.length} fixed
							<span className="text-muted-foreground">
								— {resolved.map((f) => f.title).slice(0, 3).join("; ")}
							</span>
						</p>
					)}
					{appeared.length > 0 && (
						<p className="text-sm flex items-center gap-1.5">
							<AlertCircle className="h-4 w-4 text-[hsl(var(--status-warning))]" />
							{appeared.length} new
							<span className="text-muted-foreground">
								— {appeared.map((f) => f.title).slice(0, 3).join("; ")}
							</span>
						</p>
					)}
				</div>
			)}

			{data.checklist.length === 0 ? (
				<div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
					<CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-success))]" />
					Nothing to fix on this profile right now.
				</div>
			) : (
				<div className="space-y-3">
					<p className="text-xs text-muted-foreground">
						Every check, not just the failures — “Not checked” means the data
						wasn’t available, which is not the same as passing.
					</p>
					<Tabs
						orientation="vertical"
						value={activeTopic}
						onValueChange={(next) => {
							setLocalSection(next);
							onSectionChange?.(next);
						}}
						className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]"
					>
						<div className="md:sticky md:top-4 md:self-start">
							<GbpTopicRail topics={topics} itemsByTopic={bySection} />
						</div>
						{sectionOrder.map((key) => (
							<TabsContent key={key} value={key} className="mt-0">
								{/* No heading here: the selected tab in the rail is this
								    panel's label, and repeating it would read the same
								    text out twice. */}
								{decideError && (
									<p
										role="alert"
										data-testid="gbp-decide-error"
										className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
									>
										{decideError}
									</p>
								)}
								<GbpChecklistTopic
									items={bySection[key] ?? []}
									pendingItemId={pendingItem}
									onDecide={(itemId, status) =>
										decide.mutate({ itemId, status })
									}
								/>
							</TabsContent>
						))}
					</Tabs>
				</div>
			)}

		</div>
	);
}
